import React, { useState } from 'react'
import { Box, Typography } from '@mui/material'
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline'
import { useMediaPermissions } from '../../../../shared/hooks/useMediaPermissions'
import { useTranslation } from '../../../../shared/hooks/useIntractionTranslate'
import { INTERAX_COLORS } from '../../../../constants/colors'
import { WherebyPermissionsModal } from './WherebyPermissionsModal'

/**
 * Shows a compact warning banner when camera or microphone permissions are denied.
 * Clicking the link opens a modal with browser-specific fix instructions.
 * Renders nothing when permissions are granted or still loading.
 */
export const PermissionsWarningBanner: React.FC = () => {
  const { camera, microphone, isLoading } = useMediaPermissions()
  const { t } = useTranslation()
  const [isModalOpen, setIsModalOpen] = useState(false)

  const isDenied = camera === 'denied' || microphone === 'denied'

  if (isLoading || !isDenied) return null

  return (
    <>
      <Box
        role="alert"
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 2,
          py: 1.5,
          borderRadius: '8px',
          backgroundColor: `${INTERAX_COLORS.redError}18`,
          border: `1px solid ${INTERAX_COLORS.redError}44`
        }}
      >
        <ExclamationTriangleIcon style={{ width: 16, height: 16, color: INTERAX_COLORS.redError, flexShrink: 0 }} />
        <Typography sx={{ fontSize: '13px', color: INTERAX_COLORS.white, lineHeight: 1.4 }}>
          {t('whereby.permissions.denied', 'Camera or microphone access is blocked.')}{' '}
          <Box
            component="button"
            onClick={() => setIsModalOpen(true)}
            sx={{
              background: 'none',
              border: 'none',
              padding: 0,
              cursor: 'pointer',
              fontSize: '13px',
              fontWeight: 600,
              color: INTERAX_COLORS.sun,
              textDecoration: 'underline',
              '&:hover': { opacity: 0.85 }
            }}
          >
            {t('whereby.permissions.howToFix', 'Learn how to enable them')}
          </Box>
        </Typography>
      </Box>

      <WherebyPermissionsModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </>
  )
}
